// src/pages/CredentialTransfers.js
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, formatDate, timeUntil, isManuallyDeleted, deletedLabel } from '../utils/api';
import TransferProgress from '../components/TransferProgress';
import PasswordResetModal from '../components/PasswordResetModal';

function StatusBadge({ status }) {
  const map = {
    active: ['status-active', '● Active'],
    'expired-downloaded': ['status-expired-downloaded', '✓ Viewed'],
    'expired-timeout': ['status-expired-timeout', '⏱ Timed out'],
    deleted: ['status-deleted', '🗑 Deleted'],
  };
  const [cls, label] = map[status] || ['status-deleted', status];
  return <span className={`status-badge ${cls}`}>{label}</span>;
}

function CredentialCard({ transfer, onRefresh, showToast }) {
  const [busy, setBusy] = useState('');
  const [newPassword, setNewPassword] = useState(null);
  const isActive = transfer.status === 'active';
  const isExpired = !isActive && transfer.status !== 'deleted';

  const action = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); await onRefresh(); showToast(msg || '✅ Done'); }
    catch (e) { showToast(`❌ ${e.message}`); }
    finally { setBusy(''); }
  };

  const resetPassword = async () => {
    if (!window.confirm(`Generate a new password for ${transfer.recipientEmail}? The current password will stop working immediately.`)) return;
    setBusy('reset');
    try {
      const r = await apiFetch(`credentials/${transfer.id}/reset-password`, { method: 'POST', body: '{}' });
      setNewPassword(r.password);   // no list refresh here — it could unmount this card and lose the password
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setBusy(''); }
  };

  return (
    <div className={`transfer-card${!isActive ? ' expired' : ''}`} style={{ display: 'block', padding: 0 }}>
      <div style={{ padding: '14px 18px 10px', borderBottom: '1px solid var(--g100)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px', flexWrap: 'wrap' }}>
          <div className="transfer-title">🔐 {transfer.title}</div>
          <StatusBadge status={transfer.status} />
          {transfer.hasPassword && <span className="status-badge" style={{ background: 'var(--amber-light)', color: 'var(--amber)' }}>🔑 Password protected</span>}
        </div>
        <div className="transfer-meta">
          <span>📅 Created {formatDate(transfer.createdAt)}</span>
          {isActive && <span style={{ color: 'var(--green)', fontWeight: 600 }}>⏱ {timeUntil(transfer.expiresAt)}</span>}
          {!isActive && transfer.status !== 'deleted' && transfer.expiredAt && <span>{transfer.status === 'expired-downloaded' ? '✓ Viewed' : 'Expired'} {formatDate(transfer.expiredAt)}</span>}
          {transfer.status === 'deleted' && <span style={{ color: 'var(--red)' }}>🗑 {deletedLabel(transfer)}</span>}
          <span className="tp-recipient">✉ {transfer.recipientEmail}</span>
        </div>
      </div>

      <div style={{ padding: '12px 18px' }}>
        <TransferProgress transfer={transfer} kind="credential" horizontal />
      </div>

      <div style={{ padding: '10px 18px', borderTop: '1px solid var(--g100)', background: 'var(--g50)', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {isActive && (
          <>
            <button className={`btn btn-sm ${transfer.emailStatus === 'failed' ? 'btn-primary' : 'btn-secondary'}`} disabled={!!busy}
              onClick={() => { if (!window.confirm(`Resend the notification email to ${transfer.recipientEmail}?`)) return; action('resend', () => apiFetch(`credentials/${transfer.id}/resend`, { method: 'POST', body: '{}' }), `✅ Email resent to ${transfer.recipientEmail}`); }}>
              {busy === 'resend' ? 'Sending…' : '📧 Resend email'}
            </button>
            {transfer.hasPassword && <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={resetPassword}>{busy === 'reset' ? '…' : '🔑 Reset password'}</button>}
            <button className="btn btn-secondary btn-sm" disabled={!!busy}
              onClick={() => action('extend', () => apiFetch(`credentials/${transfer.id}/extend`, { method: 'PUT', body: '{}' }), '✅ Extended 3 days')}>
              ⏱ Extend 3 days
            </button>
            <button className="btn btn-sm" style={{ background: 'var(--red-light)', color: 'var(--red)', border: '1px solid var(--red)' }}
              disabled={!!busy}
              onClick={() => action('expire', () => apiFetch(`credentials/${transfer.id}/expire`, { method: 'PUT', body: '{}' }), '✅ Expired')}>
              ✕ Expire now
            </button>
          </>
        )}
        {isExpired && (
          <>
            {transfer.status !== 'expired-downloaded' && (
              <button className="btn btn-secondary btn-sm" disabled={!!busy}
              onClick={() => action('extend', () => apiFetch(`credentials/${transfer.id}/extend`, { method: 'PUT', body: '{}' }), '✅ Re-activated')}>
              🔄 Re-activate (3 days)
            </button>
            )}
            <button className="btn btn-sm" style={{ background: 'var(--red-light)', color: 'var(--red)', border: '1px solid var(--red)' }}
              disabled={!!busy}
              onClick={() => { if (!window.confirm('Delete this credential transfer?')) return; action('delete', () => apiFetch(`credentials/${transfer.id}`, { method: 'DELETE', body: '{}' }), '✅ Deleted'); }}>
              🗑 Delete
            </button>
          </>
        )}
      </div>
      {newPassword && <PasswordResetModal password={newPassword} recipientEmail={transfer.recipientEmail} onClose={() => setNewPassword(null)} />}
    </div>
  );
}

export default function CredentialTransfers() {
  const navigate = useNavigate();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('active');
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const d = await apiFetch('credentials/list');
      setTransfers(d.transfers || []);
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const active    = transfers.filter(t => t.status === 'active');
  const completed = transfers.filter(t => t.status === 'expired-downloaded');            // viewed
  const deleted   = transfers.filter(isManuallyDeleted);                                                       // deleted on purpose
  const expired   = transfers.filter(t => t.status !== 'active' && t.status !== 'expired-downloaded' && !isManuallyDeleted(t));   // timed out / auto-purged
  const shown     = tab === 'active' ? active : tab === 'completed' ? completed : tab === 'deleted' ? deleted : expired;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <div className="page-eyebrow">Credentials</div>
          <h1 className="page-title">Credential Transfers</h1>
          <p className="page-subtitle">Passwords, API keys and sensitive text shared securely — auto-expires on first view</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/credentials/new')}>🔐 New Credential Transfer</button>
      </div>

      <div className="stat-grid">
        <div className="stat-card stat-card-red">
          <div className="stat-icon stat-icon-red">🔐</div>
          <div className="stat-info"><div className="stat-value">{transfers.length}</div><div className="stat-label">Total</div></div>
        </div>
        <div className="stat-card stat-card-green">
          <div className="stat-icon stat-icon-green">✅</div>
          <div className="stat-info"><div className="stat-value">{active.length}</div><div className="stat-label">Active</div></div>
        </div>
        <div className="stat-card stat-card-blue">
          <div className="stat-icon stat-icon-blue">👁</div>
          <div className="stat-info"><div className="stat-value">{completed.length}</div><div className="stat-label">Completed (viewed)</div></div>
        </div>
        <div className="stat-card stat-card-amber">
          <div className="stat-icon stat-icon-amber">⏱</div>
          <div className="stat-info"><div className="stat-value">{expired.filter(t => t.status === 'expired-timeout').length}</div><div className="stat-label">Expired (not viewed)</div></div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="tabs" style={{ marginBottom: 0, borderBottom: 'none' }}>
            <button className={`tab${tab === 'active' ? ' active' : ''}`} onClick={() => setTab('active')}>
              Active <span className="tab-badge">{active.length}</span>
            </button>
            <button className={`tab${tab === 'completed' ? ' active' : ''}`} onClick={() => setTab('completed')}>
              Completed <span className="tab-badge">{completed.length}</span>
            </button>
            <button className={`tab${tab === 'expired' ? ' active' : ''}`} onClick={() => setTab('expired')}>
              Expired <span className="tab-badge">{expired.length}</span>
            </button>
            <button className={`tab${tab === 'deleted' ? ' active' : ''}`} onClick={() => setTab('deleted')}>
              Deleted <span className="tab-badge">{deleted.length}</span>
            </button>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={load}>↻ Refresh</button>
        </div>
        <div className="card-body">
          {error && <div className="alert alert-error">❌ {error}</div>}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : shown.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">{tab === 'active' ? '🔐' : tab === 'completed' ? '✅' : tab === 'deleted' ? '🗑' : '🗂'}</div>
              <div className="empty-state-title">{tab === 'active' ? 'No active credential transfers' : tab === 'completed' ? 'No completed credential transfers' : tab === 'deleted' ? 'No deleted credential transfers' : 'No expired credential transfers'}</div>
              <div className="empty-state-desc" style={{ marginBottom: '16px' }}>
                {tab === 'active' ? 'Share passwords, keys and sensitive text securely.' : tab === 'completed' ? 'Transfers appear here once the recipient has viewed them.' : tab === 'deleted' ? 'Transfers you delete appear here as a record. The content itself is removed.' : 'Transfers that were not viewed in time appear here. They are kept for 30 days.'}
              </div>
              {tab === 'active' && <button className="btn btn-primary" onClick={() => navigate('/credentials/new')}>Create your first credential transfer</button>}
            </div>
          ) : (
            <div className="transfer-list">
              {shown.map(t => <CredentialCard key={t.id} transfer={t} onRefresh={load} showToast={showToast} />)}
            </div>
          )}
        </div>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
